import React, { useState, useEffect } from 'react';
import { db, database } from '../../firebase';            // Firestore (db) and RTDB (database) from firebase.js
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ref as rtdbRef, get as rtdbGet } from 'firebase/database';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';   // Import Chart.js global registry (needed for ChartJS v3/v4)
import Preloader from '../../components/Preloader';
import useTheme from '../../hooks/useTheme';

function Dashboard() {
  const { theme } = useTheme();  // Get current theme (to re-render chart on theme change if needed)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // KPI state
  const [studentCount, setStudentCount] = useState(0);
  const [professorCount, setProfessorCount] = useState(0);
  const [classCount, setClassCount] = useState(0);
  const [sessionCount, setSessionCount] = useState(0);
  const [avgAttendance, setAvgAttendance] = useState('0');

  // Chart data state
  const [sessionChartData, setSessionChartData] = useState(null);

  // Professors table data
  const [professors, setProfessors] = useState([]);  // each item: { id, name, email, numClasses, studentCount }

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all professors and students count in parallel
        const profQuery = query(collection(db, 'users'), where('role', '==', 'professor'));
        const studentQuery = query(collection(db, 'users'), where('role', '==', 'student'));
        const [profSnap, studentSnap, classesSnap, enrollSnap, ticketsSnap] = await Promise.all([
          getDocs(profQuery),
          getDocs(studentQuery),
          getDocs(collection(db, 'classes')),
          getDocs(collection(db, 'enrollments')),
          getDocs(collection(db, 'tickets'))
        ]);

        // Compute high-level counts
        const totalProfessors = profSnap.size;
        const totalStudents = studentSnap.size;
        const totalClasses = classesSnap.size;
        const totalTickets = ticketsSnap.size;
        setProfessorCount(totalProfessors);
        setStudentCount(totalStudents);
        setClassCount(totalClasses);
        // (We could also expose totalTickets as a KPI if desired, e.g., "Support Tickets: X")

        // Prepare a map of classId -> number of students
        const enrollMap = {};
        enrollSnap.docs.forEach(doc => {
          const data = doc.data();
          const count = Array.isArray(data.students) ? data.students.length : 0;
          enrollMap[doc.id] = count;
        });

        // Prepare to compute session trends and attendance
        let sessionsByMonth = {};
        let totalSessions = 0;
        let attendedCountSum = 0;
        let attendancePossibleSum = 0;

        // Fetch sessions and attendance for each class from RTDB
        for (const classDoc of classesSnap.docs) {
          const classId = classDoc.id;
          // Fetch all sessions under this class
          const [sessionsSnap, attendanceSnap] = await Promise.all([
            rtdbGet(rtdbRef(database, `sessions/${classId}`)),
            rtdbGet(rtdbRef(database, `attendance/${classId}`))
          ]);
          const sessionsData = sessionsSnap.exists() ? sessionsSnap.val() : {};
          const attendanceData = attendanceSnap.exists() ? attendanceSnap.val() : {};

          // Iterate through each session of this class
          for (const [sessionId, sessionObj] of Object.entries(sessionsData)) {
            totalSessions += 1;
            // Determine session date (assume stored as timestamp or ISO in sessionObj.start_date_time)
            const startTime = sessionObj.start_date_time;
            const sessionDate = new Date(startTime);
            if (!isNaN(sessionDate)) {
              const monthKey = sessionDate.getFullYear() + '-' + String(sessionDate.getMonth() + 1).padStart(2, '0');
              sessionsByMonth[monthKey] = (sessionsByMonth[monthKey] || 0) + 1;
            }
            // Count attendance for this session
            const classSize = enrollMap[classId] || 0;
            const attendedList = attendanceData[sessionId]?.userIds || [];  // array of userIds attended
            const attendedCount = Array.isArray(attendedList) ? attendedList.length : 0;
            attendedCountSum += attendedCount;
            attendancePossibleSum += classSize;
          }
        }

        setSessionCount(totalSessions);

        // Prepare session trend chart data (e.g., for last 6 months)
        const allMonths = Object.keys(sessionsByMonth).sort();
        const recentMonths = allMonths.slice(-6);
        const chartData = {
          labels: recentMonths,
          datasets: [
            {
              label: 'Sessions',
              data: recentMonths.map(m => sessionsByMonth[m] || 0),
              backgroundColor: 'rgba(75, 192, 192, 0.6)'  // teal-ish color with opacity
            }
          ]
        };
        setSessionChartData(chartData);

        // Calculate average attendance percentage across all sessions
        const avgAtt = attendancePossibleSum > 0 
          ? ((attendedCountSum / attendancePossibleSum) * 100).toFixed(1) 
          : '0';
        setAvgAttendance(avgAtt);

        // Compute professor metrics: number of classes and students per professor
        const profMetrics = {};
        classesSnap.docs.forEach(doc => {
          const classData = doc.data();
          const profId = classData.professor_uid;
          if (!profId) return;
          if (!profMetrics[profId]) {
            profMetrics[profId] = { numClasses: 0, totalStudents: 0 };
          }
          profMetrics[profId].numClasses += 1;
          profMetrics[profId].totalStudents += enrollMap[doc.id] || 0;
        });

        // Build professors table data
        const profRows = profSnap.docs.map(doc => {
          const u = doc.data();
          const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;  // fall back to email if name not available
          const metrics = profMetrics[doc.id] || { numClasses: 0, totalStudents: 0 };
          return {
            id: doc.id,
            name,
            email: u.email || '',
            numClasses: metrics.numClasses,
            studentCount: metrics.totalStudents
          };
        });
        // Sort professors by descending student count (busier professors first)
        profRows.sort((a, b) => b.studentCount - a.studentCount);
        setProfessors(profRows);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <Preloader />;
  }
  if (error) {
    return (
      <div className="p-4 text-center text-red-600">
        <p>Error: {error}</p>
      </div>
    );
  }

  // Theme-based chart options (ensure font colors match theme)
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: theme === 'dark' ? '#F9FAFB' : '#111827'  // text-color for legend
        }
      }
    },
    scales: {
      x: {
        ticks: { color: theme === 'dark' ? '#F9FAFB' : '#111827' },
        grid: { color: theme === 'dark' ? '#374151' : '#E5E7EB' }
      },
      y: {
        ticks: { color: theme === 'dark' ? '#F9FAFB' : '#111827' },
        grid: { color: theme === 'dark' ? '#374151' : '#E5E7EB' }
      }
    }
  };

  return (
    <section className="min-h-screen bg-[var(--bg-color)] text-[var(--text-color)] transition-colors">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-[var(--card-bg)] hover:bg-[var(--card-hover-bg)] transition-colors rounded-lg p-6 shadow text-center">
            <p className="text-xl font-medium text-[var(--muted-color)] mb-1">Total Students</p>
            <p className="text-3xl font-bold text-[var(--primary-color)]">{studentCount}</p>
          </div>
          <div className="bg-[var(--card-bg)] hover:bg-[var(--card-hover-bg)] transition-colors rounded-lg p-6 shadow text-center">
            <p className="text-xl font-medium text-[var(--muted-color)] mb-1">Total Professors</p>
            <p className="text-3xl font-bold text-[var(--primary-color)]">{professorCount}</p>
          </div>
          <div className="bg-[var(--card-bg)] hover:bg-[var(--card-hover-bg)] transition-colors rounded-lg p-6 shadow text-center">
            <p className="text-xl font-medium text-[var(--muted-color)] mb-1">Total Classes</p>
            <p className="text-3xl font-bold text-[var(--primary-color)]">{classCount}</p>
          </div>
          <div className="bg-[var(--card-bg)] hover:bg-[var(--card-hover-bg)] transition-colors rounded-lg p-6 shadow text-center">
            <p className="text-xl font-medium text-[var(--muted-color)] mb-1">Total Sessions</p>
            <p className="text-3xl font-bold text-[var(--primary-color)]">{sessionCount}</p>
          </div>
          <div className="bg-[var(--card-bg)] hover:bg-[var(--card-hover-bg)] transition-colors rounded-lg p-6 shadow text-center">
            <p className="text-xl font-medium text-[var(--muted-color)] mb-1">Avg. Attendance</p>
            <p className="text-3xl font-bold text-[var(--primary-color)]">
              {avgAttendance}<span className="text-xl">%</span>
            </p>
          </div>
          {/* You could add a KPI card for tickets if desired:
          <div className="bg-[var(--card-bg)] ...">
            <p>Support Tickets</p>
            <p className="text-3xl ...">{totalTickets}</p>
          </div>
          */}
        </div>

        {/* Sessions Over Time Chart */}
        <div className="mb-8">
          <h3 className="text-2xl font-semibold mb-4">Sessions Over Time</h3>
          <div className="chart-container bg-[var(--card-bg)] p-4 rounded-lg shadow" style={{ height: '300px' }}>
            {sessionChartData ? (
              <Bar data={sessionChartData} options={chartOptions} />
            ) : (
              <p className="text-center text-[var(--muted-color)]">No session data available.</p>
            )}
          </div>
        </div>

        {/* Professors Table */}
        <div>
          <h3 className="text-2xl font-semibold mb-4">Professors Overview</h3>
          <div className="overflow-x-auto shadow rounded-lg">
            <table className="min-w-full border-collapse bg-[var(--card-bg)]">
              <thead>
                <tr className="bg-[var(--card-hover-bg)]">
                  <th className="px-4 py-2 text-left text-[var(--text-color)] border-b border-[var(--border-color)] font-semibold">
                    Professor
                  </th>
                  <th className="px-4 py-2 text-left text-[var(--text-color)] border-b border-[var(--border-color)] font-semibold">
                    Email
                  </th>
                  <th className="px-4 py-2 text-left text-[var(--text-color)] border-b border-[var(--border-color)] font-semibold">
                    Classes
                  </th>
                  <th className="px-4 py-2 text-left text-[var(--text-color)] border-b border-[var(--border-color)] font-semibold">
                    Students
                  </th>
                </tr>
              </thead>
              <tbody>
                {professors.map(prof => (
                  <tr key={prof.id} className="hover:bg-[var(--card-hover-bg)] transition-colors">
                    <td className="px-4 py-2 border-b border-[var(--border-color)] text-[var(--text-color)]">
                      {prof.name}
                    </td>
                    <td className="px-4 py-2 border-b border-[var(--border-color)] text-[var(--text-color)]">
                      {prof.email}
                    </td>
                    <td className="px-4 py-2 border-b border-[var(--border-color)] text-[var(--text-color)]">
                      {prof.numClasses}
                    </td>
                    <td className="px-4 py-2 border-b border-[var(--border-color)] text-[var(--text-color)]">
                      {prof.studentCount}
                    </td>
                  </tr>
                ))}
                {professors.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-4 py-6 text-center text-[var(--muted-color)]">
                      No professors found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Dashboard;
